import { ENV } from '../config/env';

// Base URLs
const AUTH_BASE_URL = ENV.AUTH_API_URL;
const STORAGE_BASE_URL = ENV.STORAGE_API_URL;
const MASTER_BASE_URL = ENV.MASTER_API_URL;
const WALLET_BASE_URL = ENV.WALLET_API_URL;
const ORDER_BASE_URL = ENV.ORDER_API_URL;
const SITE_BASE_URL = ENV.SITE_API_URL;
const SUPPORT_BASE_URL = ENV.SUPPORT_API_URL;
const NOTIFICATION_BASE_URL = ENV.NOTIFICATION_API_URL;
const TASK_BASE_URL = ENV.TASK_API_URL;

const ENDPOINTS = {
  // Authentication Endpoints
  LOGIN: `${AUTH_BASE_URL}/v1/auth/login`,
  REFRESH_TOKEN: `${AUTH_BASE_URL}/v1/auth/refresh`,
  LOGOUT: `${AUTH_BASE_URL}/v1/auth/logout`,
  FORGOT_PASSWORD: `${AUTH_BASE_URL}/v1/auth/forgot-password`,
  RESET_PASSWORD: `${AUTH_BASE_URL}/v1/auth/reset-password`,
  VERIFY_EMAIL: (token: string) => `${AUTH_BASE_URL}/v1/auth/verify-email/${token}`,
  REGISTER: `${AUTH_BASE_URL}/v1/auth/advertisers`,
  SSO_CALLBACK: `${AUTH_BASE_URL}/v1/auth/sso/callback`,

  // Profile Endpoints
  GET_PROFILE: `${AUTH_BASE_URL}/v1/auth/me`,
  UPDATE_PROFILE: `${AUTH_BASE_URL}/v1/auth/me`,
  UPLOAD_AVATAR: `${AUTH_BASE_URL}/v1/auth/me/avatar`,
  CHANGE_PASSWORD: `${AUTH_BASE_URL}/v1/auth/change-password`,

  // Package Endpoints (public - no auth required)
  GET_ACTIVE_PACKAGES: `${MASTER_BASE_URL}/v1/packages/active`,
  GET_FEATURED_PACKAGES: `${MASTER_BASE_URL}/v1/packages/featured`,
  GET_PACKAGE_BY_ID: `${MASTER_BASE_URL}/v1/master/packages`,

  // Order Action Endpoints
  ORDER_APPROVE: (orderId: string) => `${ORDER_BASE_URL}/v1/orders/${orderId}/approve`,
  ORDER_REJECT: (orderId: string) => `${ORDER_BASE_URL}/v1/orders/${orderId}/reject`,
  ORDER_COMPLETE: (orderId: string) => `${ORDER_BASE_URL}/v1/orders/${orderId}/complete`,
  ORDER_CANCEL: (orderId: string) => `${ORDER_BASE_URL}/v1/orders/${orderId}/cancel`,
  ORDER_SLA_STATUS: (orderId: string) => `${ORDER_BASE_URL}/v1/orders/${orderId}/sla-status`,
  ORDER_HISTORY: (orderId: string) => `${ORDER_BASE_URL}/v1/orders/${orderId}/history`,
  // Live workflow caps read from platform Settings (max_revisions, auto_cancel_hours).
  // Consumed by `fetchOrderWorkflowConfig` in services/orderService.ts.
  ORDER_WORKFLOW_CONFIG: `${ORDER_BASE_URL}/v1/catalog/workflow`,

  // Content Order Endpoints (dedicated /v1/content-orders resource)
  CONTENT_ORDERS: `${ORDER_BASE_URL}/v1/content-orders`,
  CONTENT_ORDERS_MINE: `${ORDER_BASE_URL}/v1/content-orders/my-orders`,
  CONTENT_ORDER_DETAIL: (id: string) => `${ORDER_BASE_URL}/v1/content-orders/${id}`,
  CONTENT_ORDER_PAY: (id: string) => `${ORDER_BASE_URL}/v1/content-orders/${id}/pay`,
  CONTENT_ORDER_CANCEL: (id: string) => `${ORDER_BASE_URL}/v1/content-orders/${id}/cancel`,
  CONTENT_ORDER_HISTORY: (id: string) => `${ORDER_BASE_URL}/v1/content-orders/${id}/history`,
  CONTENT_ORDER_TIMELINE: (id: string) => `${ORDER_BASE_URL}/v1/content-orders/${id}/timeline`,
  CONTENT_ORDER_CONTENTS: (id: string) => `${ORDER_BASE_URL}/v1/content-orders/${id}/contents`,
  // Content version approve/reject on regular /v1/orders/{orderId}/contents/{contentId}/*
  CONTENT_VERSION_APPROVE: (orderId: string, contentId: string) =>
    `${ORDER_BASE_URL}/v1/orders/${orderId}/contents/${contentId}/approve`,
  CONTENT_VERSION_REJECT: (orderId: string, contentId: string) =>
    `${ORDER_BASE_URL}/v1/orders/${orderId}/contents/${contentId}/reject`,

  // Subscription Endpoints
  SUBSCRIPTION_PLANS: `${AUTH_BASE_URL}/v1/auth/subscriptions/plans`,
  SUBSCRIPTION_CURRENT: `${AUTH_BASE_URL}/v1/auth/subscriptions/current`,
  SUBSCRIPTION_HISTORY: `${AUTH_BASE_URL}/v1/auth/subscriptions/history`,
  SUBSCRIPTION_PURCHASE_WALLET: `${AUTH_BASE_URL}/v1/auth/subscriptions/purchase/wallet`,

  // Storage Base URL (for constructing avatar URLs)
  STORAGE_BASE: STORAGE_BASE_URL,

  // Chat Support Endpoints
  CHAT_ACTIVE: `${SUPPORT_BASE_URL}/v1/support/chat/active`,
  CHAT_START: `${SUPPORT_BASE_URL}/v1/support/chat/start`,
  CHAT_BASE: `${SUPPORT_BASE_URL}/v1/support/chat`,
  CHAT_ATTACHMENT: `${SUPPORT_BASE_URL}/v1/support/chat/attachment`,

  // Support Ticket Endpoints
  CREATE_SUPPORT_TICKET: `${SUPPORT_BASE_URL}/v1/support/tickets`,
  SUPPORT_TICKETS: `${SUPPORT_BASE_URL}/v1/support/tickets`,
  SUPPORT_TICKET_DETAIL: (ticketId: string) => `${SUPPORT_BASE_URL}/v1/support/tickets/${ticketId}`,
  SUPPORT_TICKET_REPLY: (ticketId: string) =>
    `${SUPPORT_BASE_URL}/v1/support/tickets/${ticketId}/reply`,
  SUPPORT_TICKET_CLOSE: (ticketId: string) =>
    `${SUPPORT_BASE_URL}/v1/support/tickets/${ticketId}/close`,
  SUPPORT_TICKET_REOPEN: (ticketId: string) =>
    `${SUPPORT_BASE_URL}/v1/support/tickets/${ticketId}/reopen`,
  SUPPORT_ATTACHMENT: (path: string) => `${SUPPORT_BASE_URL}/v1/support/attachments/${path}`,

  // Wallet Endpoints
  WALLET_BALANCE: `${WALLET_BASE_URL}/v1/wallet`,
  WALLET_TRANSACTIONS: `${WALLET_BASE_URL}/v1/wallet/transactions`,

  // Deposit Endpoints (Airwallex — Drop-in flow)
  DEPOSIT_METHODS_AVAILABLE: `${WALLET_BASE_URL}/v1/payments/methods/available`,
  DEPOSIT_INTENT: `${WALLET_BASE_URL}/v1/payments/deposit/intent`,
  DEPOSIT_HOSTED: `${WALLET_BASE_URL}/v1/payments/deposit/hosted`,
  DEPOSIT_INTENT_STATUS: (intentId: string) => `${WALLET_BASE_URL}/v1/payments/intents/${intentId}`,
  DEPOSIT_EXECUTE: `${WALLET_BASE_URL}/v1/payments/deposit/execute`,

  // Payment Consents (saved methods — optional for v1)
  CONSENTS_LIST: `${WALLET_BASE_URL}/v1/payments/consents`,
  CONSENTS_CREATE: `${WALLET_BASE_URL}/v1/payments/consents`,
  CONSENTS_DELETE: (consentId: string) => `${WALLET_BASE_URL}/v1/payments/consents/${consentId}`,

  // Deposit Endpoints (Stripe — legacy, still mounted in GatewayRegistry)
  DEPOSIT_STRIPE_INTENT: `${WALLET_BASE_URL}/v1/deposits/stripe/intent`,
  DEPOSIT_STRIPE_CONFIRM: `${WALLET_BASE_URL}/v1/deposits/stripe/confirm`,

  // Package Order Endpoints
  PACKAGE_ORDER_CREATE: `${ORDER_BASE_URL}/v1/package-orders`,
  PACKAGE_ORDERS: `${ORDER_BASE_URL}/v1/package-orders`,
  PACKAGE_ORDERS_MINE: `${ORDER_BASE_URL}/v1/package-orders/my-packages`,
  PACKAGE_ORDER_DETAIL: (id: string) => `${ORDER_BASE_URL}/v1/package-orders/${id}`,
  PACKAGE_ORDER_USAGE: (id: string) => `${ORDER_BASE_URL}/v1/package-orders/${id}/usage`,
  PACKAGE_ORDER_SLOTS: (id: string) => `${ORDER_BASE_URL}/v1/package-orders/${id}/slots`,
  PACKAGE_ORDER_ALLOCATE: (id: string) => `${ORDER_BASE_URL}/v1/package-orders/${id}/allocate`,
  PACKAGE_ORDER_PAY: (id: string) => `${ORDER_BASE_URL}/v1/package-orders/${id}/pay`,
  PACKAGE_GREY_NICHE_PRICES: `${ORDER_BASE_URL}/v1/package-grey-niche-prices`,

  // CSV Bulk Order Endpoints (under /package-orders)
  CSV_TEMPLATE_DOWNLOAD: `${ORDER_BASE_URL}/v1/package-orders/csv-template/download`,
  CSV_TEMPLATE_PARSE: `${ORDER_BASE_URL}/v1/package-orders/csv-template/parse`,

  // Generic Order Content Versioning

  // Order Endpoints
  MY_ORDERS: `${ORDER_BASE_URL}/v1/orders/my-orders`,
  CREATE_ORDER: `${ORDER_BASE_URL}/v1/orders`,
  ORDER_DETAIL: (orderId: string) => `${ORDER_BASE_URL}/v1/orders/${orderId}`,
  ORDER_PAY: (orderId: string) => `${ORDER_BASE_URL}/v1/orders/${orderId}/pay`,
  ORDER_APPROVE_BACKLINK: (orderId: string) =>
    `${ORDER_BASE_URL}/v1/orders/${orderId}/approve-backlink`,
  ORDER_REJECT_BACKLINK: (orderId: string) =>
    `${ORDER_BASE_URL}/v1/orders/${orderId}/reject-backlink`,
  ORDER_UPDATE_CONTENT: (orderId: string) => `${ORDER_BASE_URL}/v1/orders/${orderId}/content`,

  // Content Pricing Tiers (Master Data - public)
  CONTENT_PRICING_TIERS: `${MASTER_BASE_URL}/v1/content-pricing-tiers`,

  // Marketplace Endpoints
  MARKETPLACE_SITES: `${SITE_BASE_URL}/v1/marketplace/sites`,
  MARKETPLACE_FILTERS: `${SITE_BASE_URL}/v1/marketplace/filters`,

  // Deposit History
  // Live platform deposit limits — advertiser deposit modal polls this
  // to render dynamic input min/max/help-copy so admin can retune
  // wallet.deposit.min_amount / wallet.deposit.max_amount without a
  // FE redeploy.
  DEPOSIT_LIMITS: `${WALLET_BASE_URL}/v1/deposits/limits`,
  DEPOSITS_LIST: `${WALLET_BASE_URL}/v1/deposits`,
  DEPOSIT_DETAIL: (transactionId: string) => `${WALLET_BASE_URL}/v1/deposits/${transactionId}`,

  // Notification Preference Endpoints
  NOTIFICATION_PREFERENCES: `${NOTIFICATION_BASE_URL}/v1/notifications/preferences`,
  NOTIFICATION_PREFERENCES_RESET: `${NOTIFICATION_BASE_URL}/v1/notifications/preferences/reset`,
  NOTIFICATION_DEVICES: `${NOTIFICATION_BASE_URL}/v1/notifications/devices`,

  // Notification Endpoints
  NOTIFICATIONS: `${NOTIFICATION_BASE_URL}/v1/notifications`,
  NOTIFICATIONS_UNREAD_COUNT: `${NOTIFICATION_BASE_URL}/v1/notifications/unread/count`,
  NOTIFICATIONS_READ_ALL: `${NOTIFICATION_BASE_URL}/v1/notifications/read-all`,
  NOTIFICATIONS_DELETE_READ: `${NOTIFICATION_BASE_URL}/v1/notifications/read`,
  NOTIFICATION_READ: (id: string) => `${NOTIFICATION_BASE_URL}/v1/notifications/${id}/read`,
  NOTIFICATION_DELETE: (id: string) => `${NOTIFICATION_BASE_URL}/v1/notifications/${id}`,
  NOTIFICATIONS_HEARTBEAT: `${NOTIFICATION_BASE_URL}/v1/notifications/heartbeat`,
  NOTIFICATIONS_STATISTICS: `${NOTIFICATION_BASE_URL}/v1/notifications/statistics`,

  // Task Endpoints (content-writing review actions by advertiser)
  TASK_ADVERTISER_APPROVE_CONTENT: (taskId: string) =>
    `${TASK_BASE_URL}/v1/tasks/${taskId}/advertiser-approve-content`,
  TASK_ADVERTISER_REJECT_CONTENT: (taskId: string) =>
    `${TASK_BASE_URL}/v1/tasks/${taskId}/advertiser-reject-content`,
  TASK_VERIFY_BACKLINK: (taskId: string) =>
    `${TASK_BASE_URL}/v1/tasks/${taskId}/workflow/verify-backlink`,
};

// WordPress theme endpoints — /wp-json/db/v1/... (public reads; the
// vote endpoint requires user_id/email/name in the body so votes can
// be attributed and deduped per person). Served by the domainboost
// theme's inc/rest-webapp.php.
export const WP_ENDPOINTS = {
  _base: ENV.WP_API_URL,
  helpArticles: `${ENV.WP_API_URL}/wp-json/db/v1/help-articles`,
  helpArticleBySlug: (slug: string) =>
    `${ENV.WP_API_URL}/wp-json/db/v1/help-articles/${encodeURIComponent(slug)}`,
  helpArticleCategories: `${ENV.WP_API_URL}/wp-json/db/v1/help-article-categories`,
  webappFaqs: `${ENV.WP_API_URL}/wp-json/db/v1/webapp-faqs`,
  voteWebapp: `${ENV.WP_API_URL}/wp-json/db/v1/vote`,
} as const;

export default ENDPOINTS;
